import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAcceptPlan,
  useAddRecipes,
  useCurrentPlans,
  useGeneratePlans,
  useMarkCooked,
  usePlanCostTotals,
  usePlanCosts,
  usePreferences,
  useSetEntryServings,
  useSwapEntry,
} from '../api/planner';
import type { MealPlan } from '../api/planner';
import AddRecipesButton from '../components/AddRecipesButton';
import CookedButton from '../components/CookedButton';
import PlanEntryCard from '../components/PlanEntryCard';
import RefreshHealth from '../components/RefreshHealth';
import ServingsButton from '../components/ServingsButton';
import SwapButton from '../components/SwapButton';

export default function Plan() {
  const { data, isLoading } = useCurrentPlans();
  const generate = useGeneratePlans();
  const accept = useAcceptPlan();
  const swap = useSwapEntry();
  const cooked = useMarkCooked();
  const setServings = useSetEntryServings();
  const add = useAddRecipes();
  const { data: preferences } = usePreferences();

  const plans = data?.results ?? [];
  const [storeId, setStoreId] = useState<number | null>(null);

  // A week that has been shopped for is settled: the food is bought, so it can
  // be added to but not re-chosen. Any plan in the week, not just the one on
  // screen -- generating rebuilds every active store's plan and clears each
  // one's shopped_at, so a shop recorded against one is destroyed by a redo
  // launched from another store's tab.
  //
  // Boolean() rather than a null check: the query cache is offlineFirst, and a
  // plan cached before shopped_at existed has no key at all.
  const settledPlan = plans.find((option) => Boolean(option.shopped_at));
  const weekSettled = Boolean(settledPlan);

  // An accepted plan is the week; otherwise show the preferred store.
  useEffect(() => {
    if (storeId !== null || plans.length === 0) return;
    const accepted = plans.find((plan) => plan.status === 'accepted');
    setStoreId((accepted ?? plans[0]).store.id);
  }, [plans, storeId]);

  // Resolved before the early returns below, because hooks cannot be skipped.
  const shown: MealPlan | undefined =
    plans.find((option) => option.store.id === storeId) ?? plans[0];
  const { data: costs } = usePlanCosts(shown?.id);
  const tabTotals = usePlanCostTotals(plans.map((option) => option.id));

  const costOf = (recipeId: number) =>
    costs?.recipes.find((row) => row.recipe_id === recipeId)?.cost ?? '';

  if (isLoading) return <p className="center-note">Loading your meal plan…</p>;

  if (plans.length === 0) {
    return (
      <section>
        <h1>This week</h1>
        <p className="muted">No suggestions yet for this week.</p>
        {/* Most likely to matter here: a week with nothing in it is often a
            week whose price refresh did not run. */}
        <RefreshHealth />
        <button
          className="btn btn--primary btn--wide"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
        >
          {generate.isPending ? 'Thinking…' : 'Plan meals for the week'}
        </button>
        {generate.isError && (
          <p className="error">{(generate.error as Error).message}</p>
        )}
        {/* A week with nothing suggested in it is the likeliest moment to
            want the last one, so the way back cannot live only below a plan
            that isn't there. */}
        <Link className="btn btn--wide" to="/history">
          Past weeks →
        </Link>
      </section>
    );
  }

  const plan = shown;
  const isAccepted = plan?.status === 'accepted';

  return (
    <section>
      <h1>This week</h1>

      {/* One plan per store: what's worth cooking depends on where you shop. */}
      <div className="store-tabs" role="tablist">
        {plans.map((option) => (
          <button
            key={option.id}
            role="tab"
            aria-selected={option.store.id === plan?.store.id}
            className={`store-tab ${option.store.id === plan?.store.id ? 'is-active' : ''}`}
            onClick={() => setStoreId(option.store.id)}
          >
            {option.store.name}
            {option.status === 'accepted' && <span aria-label="accepted"> ✓</span>}
            {/* The week's total, on the tab that chooses it: comparing shops
                is the reason to have tabs, and the price was two clicks away
                on each one. Absent while it is still being priced, and for a
                store that could not be planned at all. */}
            {tabTotals[option.id] && (
              <span className="store-tab__price">${tabTotals[option.id]}</span>
            )}
          </button>
        ))}
      </div>

      <RefreshHealth />

      {/* Nothing to act on once the week is bought: the prices may well have
          moved, but re-choosing is no longer something the kitchen can do. */}
      {plan?.stale && !weekSettled && (
        <div className="card notice">
          {/* Both inputs, in one sentence and no diff: a plan goes out of date
              when preferences change and when the shop's prices are refreshed,
              and naming only the first made the message wrong half the time. */}
          <p>This week was chosen before your latest preferences and prices.</p>
          <button
            className="btn btn--primary"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
          >
            {generate.isPending ? 'Choosing…' : 'Choose again'}
          </button>
        </div>
      )}

      {plan?.blocked_reason && (
        <div className="card notice notice--blocked">
          <p>{plan.blocked_reason}</p>
        </div>
      )}

      {plan && !plan.blocked_reason && (
        <>
          {/* The summary of the week belongs above the week, not under it. */}
          {plan.generation_notes && (
            <p className="plan-summary">{plan.generation_notes}</p>
          )}

          {costs?.total && (
            <p className="plan-total">
              <span className="plan-total__amount">${costs.total}</span>
              <span className="muted small">
                {' '}
                for the week at {costs.store}
                {costs.unpriced_lines > 0 &&
                  ` · ${costs.unpriced_lines} item${
                    costs.unpriced_lines === 1 ? '' : 's'
                  } not priced`}
              </span>
            </p>
          )}

          <ol className="plan-list">
            {plan.entries.map((entry) => {
              // This dinner's own size where it has one. `??` rather than
              // `||`: a plan cached before the field existed has no key, and
              // the week's figure is the right answer for it.
              const planned = entry.servings ?? plan.servings;
              return (
                <PlanEntryCard
                  key={entry.id}
                  entry={entry}
                  cost={costOf(entry.recipe.id)}
                  actions={
                    <>
                      {/* Gone once the shopping is bought: the food for this
                          dinner is in the cupboard, so there is nothing a
                          swap could honestly do with it. */}
                      {!weekSettled && (
                        <SwapButton
                          recipeTitle={entry.recipe.title}
                          pending={swap.isPending}
                          error={
                            swap.isError ? (swap.error as Error).message : undefined
                          }
                          onSwap={(choice, done) =>
                            swap.mutate(
                              { entryId: entry.id, ...choice },
                              { onSuccess: done },
                            )
                          }
                        />
                      )}
                      <ServingsButton
                        recipeTitle={entry.recipe.title}
                        plannedServings={planned}
                        weekServings={plan.servings}
                        shopped={weekSettled}
                        pending={setServings.isPending}
                        onChange={(servings) =>
                          setServings.mutate({ entryId: entry.id, servings })
                        }
                      />
                      <CookedButton
                        recipeTitle={entry.recipe.title}
                        plannedServings={planned}
                        pending={cooked.isPending}
                        onCook={(servings) =>
                          cooked.mutate({
                            entryId: entry.id,
                            // Omitted when it matches, so the entry stays the
                            // one place the ordinary number is written down.
                            servings: servings === planned ? undefined : servings,
                          })
                        }
                      />
                    </>
                  }
                />
              );
            })}
          </ol>

          <div className="plan-actions">
            {isAccepted ? (
              <Link className="btn btn--primary btn--wide" to="/list">
                Shopping list →
              </Link>
            ) : (
              <button
                className="btn btn--primary btn--wide"
                onClick={() => accept.mutate(plan.id)}
                disabled={accept.isPending}
              >
                {accept.isPending
                  ? 'Saving…'
                  : `Cook this week from ${plan.store.name}`}
              </button>
            )}
            {/* Redoing a week is throwing it away and choosing again, which
                is fine right up until the shopping is in the cupboard. After
                that the useful question is the opposite one: you ran through
                the week early and need another dinner before the next one. */}
            {!weekSettled ? (
              <button
                className="btn btn--wide"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
              >
                {generate.isPending ? 'Choosing…' : 'Redo this week’s meal plan'}
              </button>
            ) : plan.id === settledPlan!.id ? (
              <AddRecipesButton
                storeName={plan.store.name}
                defaultServings={preferences?.servings ?? plan.servings}
                pending={add.isPending}
                error={add.isError ? (add.error as Error).message : undefined}
                onAdd={(choice, done) =>
                  add.mutate({ planId: plan.id, ...choice }, { onSuccess: done })
                }
              />
            ) : (
              /* An alternate on a settled week is the record of the shop not
                 taken. Adding a dinner to it would put food on a plan nobody
                 is cooking from, so the way forward is the tab that was. */
              <>
                <p className="muted small">
                  You shopped this week from {settledPlan!.store.name}.
                </p>
                <button
                  className="btn btn--wide"
                  onClick={() => setStoreId(settledPlan!.store.id)}
                >
                  Go to {settledPlan!.store.name} →
                </button>
              </>
            )}
            <Link className="btn btn--wide" to="/history">
              Past weeks →
            </Link>
          </div>
        </>
      )}
    </section>
  );
}
